import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import sum from 'lodash/sum';

import iconUp from '~Assets/icons/navigate-left.svg';
import Icon from '~Scenes/SelfServiceKiosks/Components/Icon';
import Text from '~Scenes/SelfServiceKiosks/Components/Text';
import { Choice, Order } from '../../..';
import { useCurrency, useI18n } from '~Hooks';
import ChoiceItem from './ChoiceItem';

const Wrapper = styled.div`
  background: rgb(23, 23, 26);
  border-radius: 8px;
  box-shadow: 0px 7px 8px -4px rgb(0 0 0 / 20%), 0px 12px 17px 2px rgb(0 0 0 / 14%),
    0px 5px 22px 4px rgb(0 0 0 / 12%);
  width: 496px;
  margin: 0 auto 24px;
  position: relative;
`;
const ToggleAction = styled.div<{ active: boolean }>`
  padding: 20px 0;
  border-bottom: 1px solid ${({ active }) => (active ? 'transparent' : 'rgb(66, 66, 69)')};
`;
const IconWrapper = styled.div<{ active: boolean }>`
  background: rgb(33, 33, 36);
  height: 40px;
  width: 40px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  transform: rotate(${({ active }) => (active ? '270' : '90')}deg);
  transition: 0.3s transition;
`;
const TotalPrice = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ active }) => (active ? 'rgb(33, 33, 36)' : '#fff')};
  padding: 21px 24px 26px;
  background: ${({ active }) => (active ? 'rgb(245, 245, 248)' : 'rgb(23, 23, 26)')};
  border-radius: 0 0 8px 8px;
  transition: 0.3s background, 0.3s color;
`;
const Title = styled.span`
  display: flex;
  align-items: center;
  font-family: Eina01-SemiBold;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 32px;
`;
const Counter = styled.div<{ active: boolean }>`
  background: rgb(255, 255, 255);
  height: 24px;
  min-width: 24px;
  border-radius: 12px;
  color: rgb(66, 66, 69);
  font-family: Eina01-SemiBold;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 22px;
  text-align: center;
  margin-left: 8px;
  opacity: ${({ active }) => (active ? 0 : 1)};
  transition: 0.2s opacity;
`;
export const Price = styled.span<{ selected?: boolean }>`
  font-family: Eina01-Regular;
  font-size: 24px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 32px;
  text-align: right;
  white-space: nowrap;
  transition: 0.3s color, 0.3s opacity;
  color: ${({ selected }) => (selected ? '#cccccc' : 'rgb(33, 33, 36)')};
`;
const OrderList = styled.div<{ active: boolean }>`
  height: 100%;
  background: #fff;
  max-height: ${({ active }) => (active ? 435 : 0)}px;
  transition: 0.3s max-height;
  overflow: auto;
  & > div:last-child {
    border: none;
  }
`;
export const Item = styled.div`
  padding: 24px 0;
  border-bottom: 1px solid rgb(238, 238, 241);
  margin: 0 24px;
`;
export const Row = styled.div<{ secondary?: boolean; removed?: boolean }>`
  letter-spacing: 0px;
  display: flex;
  justify-content: space-between;

  ${({ secondary, removed }) => {
    if (secondary) {
      return `
        margin: 8px 0 0 40px;
        color: ${removed ? '#e3293b' : 'rgb(97, 97, 100)'};
        font-family: Eina01-Regular;
        font-size: 16px;
        font-weight: normal;
        line-height: 24px;
      `;
    }
    return `
      color:rgb(33, 33, 36);
      font-family: Eina01-SemiBold;
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
    `;
  }}
`;

interface Props {
  order: Order;
  price: number;
  choicesArr: Choice[];
  active: boolean;
  setActive: React.Dispatch<React.SetStateAction<boolean>>;
  onRemoveUpsellItem: (id: string) => void;
}

const Basket = ({ order, price, choicesArr, active, setActive, onRemoveUpsellItem }: Props) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const quantity = order.quantity + choicesArr.length;
  const expandOrderList = () => {
    setActive((prev) => !prev);
  };

  const itemPrice = useMemo(
    () => sum([order.item.amount, ...order.selectedVariations.map((sv) => sv.price)]),
    [order],
  );

  return (
    <Wrapper>
      <ToggleAction onClick={expandOrderList} active={active}>
        <IconWrapper active={active}>
          <Icon icon={iconUp} size={32} color="#fff" />
        </IconWrapper>
      </ToggleAction>
      <OrderList active={active}>
        {Array.from(Array(order.quantity).keys()).map((key) => {
          return (
            <Item key={key}>
              <Row>
                <Text line={2}>{order.item.title}</Text>
                <Price>{currency.format(itemPrice)}</Price>
              </Row>
              {order.selectedVariations.map(({ id, name }) => (
                <Row key={id} secondary>
                  <Text line={3}>{name}</Text>
                </Row>
              ))}
              {order.removedVariations.map(({ id, name }) => (
                <Row key={id} secondary removed>
                  <Text line={3}>{`- ${name}`}</Text>
                </Row>
              ))}
            </Item>
          );
        })}

        {choicesArr.map((choice, key) => (
          <ChoiceItem key={key} choice={choice} onRemoveUpsellItem={onRemoveUpsellItem} />
        ))}
      </OrderList>
      <TotalPrice active={active}>
        <Title>
          {i18n('Express.Cart.Title.YourChoices')} <Counter active={active}>{quantity}</Counter>
        </Title>
        <Price>{currency.format(price)}</Price>
      </TotalPrice>
    </Wrapper>
  );
};

export default Basket;
